import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article';
  structuredData?: object | object[];
  canonical?: string;
  noIndex?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

const SEO = ({
  title,
  description,
  keywords = 'promozioni, offerte, sconti, cashback, rimborsi, Italia, risparmio, shopping',
  image = 'https://lovable.dev/opengraph-image-p98pqg.png',
  type = 'website',
  structuredData,
  canonical,
  noIndex = false,
  author = 'AffareFatto',
  publishedTime,
  modifiedTime
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = 'https://www.affarefatto.info';
  
  // Normalize URL: remove trailing slash (except for root)
  const normalizedPath = location.pathname === '/' 
    ? '/' 
    : location.pathname.replace(/\/+$/, '');
  const fullUrl = canonical || `${baseUrl}${normalizedPath}`;
  
  // Limit title to 55 chars for SEO (leaving room for " | AffareFatto")
  const maxTitleLength = 40; // 40 + " | AffareFatto" (15) = 55 chars max
  const truncatedTitle = title.length > maxTitleLength ? `${title.substring(0, maxTitleLength)}...` : title;
  const fullTitle = `${truncatedTitle} | AffareFatto`;
  
  // Limit description to 160 chars for SEO
  const truncatedDescription = description.length > 155 
    ? `${description.substring(0, 155)}...` 
    : description;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', truncatedDescription);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    
    // Robots
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1');
    }

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', truncatedDescription, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:locale', 'it_IT', true);
    updateMetaTag('og:site_name', 'AffareFatto', true);
    
    // Article specific tags
    if (type === 'article') {
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime, true);
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime, true);
      }
      updateMetaTag('article:author', author, true);
    }

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', truncatedDescription);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:site', '@AffareFatto');

    // Additional SEO tags
    updateMetaTag('format-detection', 'telephone=no');
    updateMetaTag('theme-color', '#4CAF50');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Structured Data
    if (structuredData) {
      // Remove old dynamic SEO scripts
      document.querySelectorAll('script[type="application/ld+json"].dynamic-seo').forEach(el => el.remove());
      
      const dataArray = Array.isArray(structuredData) ? structuredData : [structuredData];
      dataArray.forEach((data, index) => {
        const scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.classList.add('dynamic-seo');
        scriptTag.setAttribute('id', `dynamic-seo-${index}`);
        scriptTag.textContent = JSON.stringify(data);
        document.head.appendChild(scriptTag);
      });
    }

    // Add hreflang for Italian content
    let hreflangIt = document.querySelector('link[hreflang="it"]');
    if (!hreflangIt) {
      hreflangIt = document.createElement('link');
      hreflangIt.setAttribute('rel', 'alternate');
      hreflangIt.setAttribute('hreflang', 'it');
      document.head.appendChild(hreflangIt);
    }
    hreflangIt.setAttribute('href', fullUrl);

    // x-default hreflang
    let hreflangDefault = document.querySelector('link[hreflang="x-default"]');
    if (!hreflangDefault) {
      hreflangDefault = document.createElement('link');
      hreflangDefault.setAttribute('rel', 'alternate');
      hreflangDefault.setAttribute('hreflang', 'x-default');
      document.head.appendChild(hreflangDefault);
    }
    hreflangDefault.setAttribute('href', fullUrl);

  }, [title, description, keywords, image, type, fullUrl, structuredData, fullTitle, truncatedDescription, noIndex, author, publishedTime, modifiedTime]);

  return null;
};

export default SEO;
